<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { FlowLayout } from '@Shared';
import { reprofileDeviceVerification } from 'host/mfeBridge';
import { reportVpnStepUpDisplayApi } from '@Api/authFlow.api';
import useFlowNavigation from '@Use/useFlowNavigation';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import { useAuthFlowStore } from '@Stores/authFlow.store';
import useCompanyImageBaseUrl from '@Use/useCompanyImageBaseUrl';
import LockedShield from '@Assets/images/locked-shield.svg';

// This view's own route name, as registered in the router and in
// FLOW_DEFINITIONS for the DisableVpn step.
const DISABLE_VPN_ROUTE_NAME = 'disable-vpn';

interface DisableVpnCopy {
    title: string;
    paragraphs: string[];
    primaryLabel: string;
}

// Copy variants keyed by how many times the participant has been shown this
// step-up. The ordinal lives in the authFlow store and defaults to the first
// display; the display report below populates it from the backend's
// authoritative count.
const COPY_BY_DISPLAY: Record<1 | 2 | 3, DisableVpnCopy> = {
    1: {
        title: 'Turn off your VPN to continue',
        paragraphs: [
            'We detected a VPN on your connection. To verify your identity and protect this transaction, we need to see your real connection.',
            "Turn off your VPN, then continue. You can turn it back on as soon as you're done.",
        ],
        primaryLabel: 'Continue',
    },
    2: {
        title: 'Your VPN is still active',
        paragraphs: [
            'A VPN may still be running in the background. Check your device settings and any application that manages your connection, then try again.',
        ],
        primaryLabel: 'Try Again',
    },
    3: {
        title: 'Your VPN is still active',
        paragraphs: [
            'A VPN is still active on your connection. If we detect one on your next attempt, this task will pause and move to our team for review.',
            'Check your device settings and any application that manages your connection before trying again.',
        ],
        primaryLabel: 'Try Again',
    },
};

// The connection check came back inconclusive (profiling timed out or
// failed). This is our infrastructure failing, not evidence the VPN is
// still on — a still-detected VPN can only surface from the next RDS
// evaluation — so the copy must not claim detection, and the participant
// can simply try again.
const INCONCLUSIVE_COPY: DisableVpnCopy = {
    title: "We couldn't check your connection",
    paragraphs: [
        "We weren't able to confirm your connection just now. This was a problem on our end, not something you did.",
        'Make sure your VPN is turned off, then try again.',
    ],
    primaryLabel: 'Try Again',
};

const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const authFlowStore = useAuthFlowStore();
const companyImageBaseUrl = useCompanyImageBaseUrl();
const { advanceFlow, continueMainFlow } = useFlowNavigation();

const isRetrying = ref(false);
const isInconclusive = ref(false);

// Report a display of this step-up to the BFF. Each report carries a fresh
// display id, so every rendering counts exactly once. On failure the API
// resolves undefined and the ordinal simply keeps its current value; on
// success the backend's attempt ordinal drives the copy variant reactively
// through the store.
const reportDisplay = async () => {
    const report = await reportVpnStepUpDisplayApi(crypto.randomUUID());
    if (report) {
        authFlowStore.setVpnStepUpDisplayOrdinal(report.attempt);
    }
};

// Fire-and-forget on mount: the render never waits on the report, and a
// refresh remounts and reports as a new display.
onMounted(reportDisplay);

const content = computed<DisableVpnCopy>(() => {
    if (isInconclusive.value) {
        return INCONCLUSIVE_COPY;
    }
    const ordinal = authFlowStore.vpnStepUpDisplayOrdinal;
    if (ordinal >= 3) {
        return COPY_BY_DISPLAY[3];
    }
    return ordinal === 2 ? COPY_BY_DISPLAY[2] : COPY_BY_DISPLAY[1];
});

// Primary action: the participant says the VPN is off. Ask the host to
// re-profile the device and gate on backend evidence readiness. Only a
// 'ready' result re-enters evaluation — clearing the flow sequence and
// advancing re-fetches the next auth flow, so the fresh RDS evaluation
// decides whether the gate clears (same resume mechanics the IDV
// processing view uses). 'timedOut'/'failed' surface the inconclusive
// state on this already-mounted view and never re-evaluate against an
// unpopulated profile.
const handlePrimary = async () => {
    isRetrying.value = true;
    isInconclusive.value = false;
    try {
        const result = await reprofileDeviceVerification();
        if (result === 'ready') {
            // The router guard persisted this route as the flow cursor; clear
            // both the sequence and the cursor so a re-emitted DisableVpn step
            // is treated as the next task (shown again) rather than the
            // already-current one (skipped past, bypassing the gate).
            authFlowStore._clearFlowSequence();
            authFlowStore.clearCurrentRoute();
            // A failed evaluation fetch must not read as clearance — stay on
            // the gate in the inconclusive state and let the participant retry.
            const outcome = await advanceFlow({ haltOnFetchFailure: true });
            if (outcome !== 'fetch-failed') {
                if (outcome === 'navigated' && authFlowStore.currentRoute === DISABLE_VPN_ROUTE_NAME) {
                    // The step was re-emitted onto the route we already
                    // occupy, so the view is reused rather than remounted and
                    // the mount-time report never fires — this re-display must
                    // be reported here. The flow cursor is set synchronously
                    // during advanceFlow; the router's current route is not.
                    await reportDisplay();
                }
                return;
            }
        }
        isInconclusive.value = true;
    } catch (err) {
        console.error('[Auth MFE] reprofileDeviceVerification failed:', err);
        isInconclusive.value = true;
    } finally {
        isRetrying.value = false;
    }
};

// Secondary action: the participant cannot turn off their VPN. Per design,
// this lets them complete their task through the main flow; RDS decisions
// the request at the end (manual review), so we deliberately skip
// re-evaluation here rather than looping them back into this step-up.
const handleCantDisable = () => {
    continueMainFlow();
};
</script>

<template>
    <flow-layout
        suppress-back-to-dashboard
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl"
        :show-back-button="false">

        <app-form-content-layout>
            <div class="disable-vpn-view">
                <img
                    alt=""
                    class="disable-vpn-view__graphic"
                    :src="LockedShield" />
                <div class="disable-vpn-view__text-container">
                    <h1 data-id="disable-vpn-title">{{ content.title }}</h1>
                    <p
                        v-for="(paragraph, index) in content.paragraphs"
                        :key="paragraph"
                        :data-id="`disable-vpn-paragraph-${index + 1}`">
                        {{ paragraph }}
                    </p>
                </div>

                <div class="disable-vpn-view__actions">
                    <app-button
                        expanded
                        size="large"
                        type="primary"
                        :is-loading="isRetrying"
                        data-id="disable-vpn-primary"
                        @onClick="handlePrimary">
                        {{ content.primaryLabel }}
                    </app-button>
                    <app-button
                        expanded
                        size="large"
                        type="outline"
                        :is-disabled="isRetrying"
                        data-id="disable-vpn-cant-turn-off"
                        @onClick="handleCantDisable">
                        I can't turn off my VPN
                    </app-button>
                </div>
            </div>
        </app-form-content-layout>
    </flow-layout>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.disable-vpn-view {
    display: flex;
    flex-direction: column;
    height: 100%;

    &__graphic {
        width: 5.8rem;
        height: 7rem;
        align-self: center;
        margin-bottom: 1.5rem;
    }

    &__text-container {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        color: $black-high;

        p {
            line-height: 1.5;
        }
    }

    &__actions {
        margin-top: 2rem;
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }
}
</style>
